// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";

interface ProductListGridContainerContextType {
    styles: ProductListGridContainerStyle;
}

export interface ProductListGridContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
    priceAvailabilityLink?: InjectableCss;
    priceAvailabilityContainer?: InjectableCss;
}

export const productListGridContainerStyle: ProductListGridContainerStyle = {};

const ProductListGridContainerContext = React.createContext<ProductListGridContainerContextType>({
    styles: {},
});

const sharedClassName = "product-grid-container";

const enum NAMES {
    Image = "Image",
    Info = "Info",
    AddToCartActions = "AddToCartActions",
    Price = "Price",
    Availability = "Availability",
    AddToList = "AddToList",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructedChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Info),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AddToList),
    ];
};

const ProductListGridContainer = (props: {
    children: any;
    extendedStyles?: ProductListGridContainerStyle;
    [key: string]: any;
}) => {
    const { children, extendedStyles, ...otherProps } = props;
    const [styles] = useState(() => mergeWithCss(productListGridContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [image, info, addToCartActions, price, availability, addToList] = React.useMemo(
        () => getDestructedChildren(childrenArray),
        [childrenArray],
    );
    return (
        <ProductListGridContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} className={sharedClassName} {...otherProps}>
                <StyledWrapper className="top-section">{image}</StyledWrapper>
                <StyledWrapper className="middle-section">{info}</StyledWrapper>
                <StyledWrapper className="bottom-section">
                    {price}
                    {availability}
                    {addToCartActions}
                    {addToList}
                </StyledWrapper>
            </StyledWrapper>
        </ProductListGridContainerContext.Provider>
    );
};

export const useProductListGridContainerContext = () => {
    const context = React.useContext(ProductListGridContainerContext);
    if (!context) {
        throw new Error("ProductGridTemplate compound components cannot be rendered outside ProductListGridContainer");
    }
    return context;
};

const Image: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductListGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductListGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info} className={`${sharedClassName}-info`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToCartActions },
    } = useProductListGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductListGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} data-test-selector="productGrid-AddToListActions">
            {children}
        </StyledWrapper>
    );
};

const Price: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductListGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC<PropsWithChildren> = ({ children, ...otherProps }) => {
    const {
        styles: { availability },
    } = useProductListGridContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};

ProductListGridContainer.Image = Image;
ProductListGridContainer.Image.displayName = NAMES.Image;
ProductListGridContainer.Info = Info;
ProductListGridContainer.Info.displayName = NAMES.Info;
ProductListGridContainer.AddToCartActions = AddToCartActions;
ProductListGridContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductListGridContainer.Price = Price;
ProductListGridContainer.Price.displayName = NAMES.Price;
ProductListGridContainer.Availability = Availability;
ProductListGridContainer.Availability.displayName = NAMES.Availability;
ProductListGridContainer.AddToList = AddToList;
ProductListGridContainer.AddToList.displayName = NAMES.AddToList;

export default ProductListGridContainer;
